import React, { useEffect, useState } from "react";
import { NavigateButton } from "@/components/NavigateButton";
import { TopRatedBadge } from "@/components/TopRatedBadge";
import { SeatingBadge, vipFeeText } from "@/components/Seating";
import { EventReviews } from "@/components/EventReviews";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CalendarDays, MapPin, Navigation, Moon, Mic2 } from "lucide-react";
import { AppHeader } from "@/components/AppHeader";
import { StatusBadge, PriceTag, evTitle } from "@/components/EventCard";
import { BookingDialog, optLabel } from "@/components/BookingDialog";
import { VenueMap } from "@/components/VenueMap";
import { HeartButton } from "@/components/HeartButton";
import { CalendarMenu } from "@/components/CalendarMenu";
import { EventShareButtons } from "@/components/ShareButtons";
import { api } from "@/lib/api";
import { imgSrc } from "@/lib/img";
import { fmtDateTime } from "@/lib/time";
import { usePrefs } from "@/contexts/PrefsContext";
import { useAuth } from "@/contexts/AuthContext";
import { useSections } from "@/lib/sections";
import type { AppEvent } from "@/types";

const Row = ({ label, children, id }: { label: string; children: React.ReactNode; id: string }) => (
  <div data-testid={id}><p className="text-xs font-bold text-muted-foreground">{label}</p><div className="text-sm leading-relaxed">{children}</div></div>
);

export default function EventDetails() {
  const { id } = useParams();
  const { t, lang } = usePrefs();
  const sec = useSections();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [e, setE] = useState<AppEvent | null | false>(null);
  const [open, setOpen] = useState(false);
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    api.get(`/events/${id}`).then((r) => setE(r.data)).catch(() => setE(false));
  }, [id]);
  useEffect(() => {
    if (params.get("book") !== "1" || !e) return;
    if (e.request_status === "open" && user && user.role === "customer") setOpen(true);
    if (user || e.request_status !== "open") setParams({}, { replace: true });
  }, [e, user]); // eslint-disable-line react-hooks/exhaustive-deps

  const book = () => {
    if (!user) {
      localStorage.setItem("wn_after_login", `/events/${id}?book=1`);
      toast(t("booking.loginNeeded"));
      navigate("/login");
    } else if (user.role !== "customer") toast(t("booking.loginNeeded"));
    else setOpen(true);
  };

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      <main className="mx-auto max-w-5xl px-4 py-5 pb-16" data-testid="event-details-page">
        {e === false && <p className="text-center py-20 text-muted-foreground" data-testid="event-not-found">{t("ev.empty")}</p>}
        {e === null && <div className="aspect-video rounded-[2rem] bg-muted animate-pulse" />}
        {e && (
          <div className="grid gap-8 lg:grid-cols-[1.1fr_0.9fr]">
            <div className="space-y-6">
              <div className="relative overflow-hidden rounded-[2rem] shadow-xl">
                {e.poster || e.venue.image ? <img src={imgSrc(e.poster || e.venue.image || "")} alt={evTitle(e, lang)} className="w-full max-h-[480px] object-cover" />
                  : <div data-testid="event-no-poster" className="aspect-video grid place-items-center bg-accent"><CalendarDays className="h-16 w-16 text-primary/50" /></div>}
                <StatusBadge status={e.request_status} className="absolute top-4 start-4" />
                <HeartButton eventId={e.event_id} className="absolute top-4 end-4" />
              </div>
              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-accent px-3 py-1 text-xs font-bold">{lang === "ar" ? e.type_name_ar : e.type_name_en}</span>
                  {e.top_rated && <TopRatedBadge rating={e.venue_rating} testId="event-top-rated" />}
                </div>
                <h1 data-testid="event-title" className="font-display text-3xl sm:text-4xl font-black leading-tight">{evTitle(e, lang)}</h1>
                {sec.spots ? <Link to={`/venues/${e.venue.venue_id}`} data-testid="event-venue-link" className="inline-flex items-center gap-1 text-sm font-bold text-primary"><MapPin className="h-4 w-4" />{lang === "ar" ? e.venue.name_ar : e.venue.name_en}</Link>
                  : <span data-testid="event-venue-name" className="inline-flex items-center gap-1 text-sm font-bold"><MapPin className="h-4 w-4" />{lang === "ar" ? e.venue.name_ar : e.venue.name_en}</span>}
                <p className="text-muted-foreground">{lang === "ar" ? e.short_ar : e.short_en || e.short_ar}</p>
              </div>
              <div className="flex flex-wrap gap-2" data-testid="event-actions">
                <CalendarMenu eventId={e.event_id} title={evTitle(e, lang)} start={e.start_at} end={e.end_at}
                  location={[lang === "ar" ? e.venue.name_ar : e.venue.name_en || e.venue.name_ar, e.address].filter(Boolean).join(", ")} />
                <EventShareButtons eventId={e.event_id} title={evTitle(e, lang)} date={fmtDateTime(e.start_at, lang, { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })} />
              </div>
              {(lang === "ar" ? e.details_ar : e.details_en || e.details_ar) && <p className="whitespace-pre-line leading-relaxed">{lang === "ar" ? e.details_ar : e.details_en || e.details_ar}</p>}
              {e.programs.length > 0 && (
                <section className="space-y-3" data-testid="event-programs">
                  <h2 className="font-display text-lg font-bold">{lang === "ar" ? e.program_label_ar : e.program_label_en}</h2>
                  {[...e.programs].sort((a, b) => (a.time || "99").localeCompare(b.time || "99")).map((p, i) => (
                    <div key={i} className="flex items-center gap-3 rounded-2xl border bg-card p-3">
                      <div className="h-10 w-10 rounded-xl bg-primary/10 text-primary grid place-items-center"><Mic2 className="h-5 w-5" /></div>
                      <div className="flex-1"><p className="font-bold">{p.name}</p><p className="text-xs text-muted-foreground">{t(`prog.${p.type}`)}</p></div>
                      {p.time && <span className="text-sm font-bold" dir="ltr">{p.time}</span>}
                    </div>
                  ))}
                </section>
              )}
              <EventReviews eventId={e.event_id} />
              <section className="space-y-3">
                <h2 className="font-display text-lg font-bold">{t("ev.location")}</h2>
                {typeof e.venue.lat === "number" && typeof e.venue.lng === "number" && <VenueMap lat={e.venue.lat} lng={e.venue.lng}/>}
                <p className="text-sm text-muted-foreground" data-testid="event-address">{[lang === "ar" ? e.area : e.area_en || e.area, e.address].filter(Boolean).join(" · ")}</p>
                <NavigateButton lat={e.venue.lat} lng={e.venue.lng} name={e.venue.name_ar} fallbackUrl={e.maps_url} testId="event-directions" className="inline-flex h-11 px-5 items-center gap-2 rounded-full bg-accent font-bold text-sm"><Navigation className="h-4 w-4" />{t("ev.directions")}</NavigateButton>
              </section>
            </div>
            <aside className="space-y-4 lg:sticky lg:top-24 self-start">
              <div className="rounded-3xl border bg-card p-5 space-y-4 shadow-sm">
                <Row label={t("ev.schedule")} id="event-schedule">
                  <span className="inline-flex items-center gap-1.5 font-bold"><CalendarDays className="h-4 w-4" />{fmtDateTime(e.start_at, lang, { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" })}</span>
                  <br />→ {fmtDateTime(e.end_at, lang, { weekday: e.ends_next_day ? "long" : undefined, hour: "2-digit", minute: "2-digit" })}
                  {e.ends_next_day && <span data-testid="event-ends-next-day" className="ms-2 inline-flex items-center gap-1 rounded-full bg-amber-400/20 px-2 py-0.5 text-xs font-bold"><Moon className="h-3 w-3" />{t("ev.endsNextDay")}</span>}
                </Row>
                <Row label={t("ev.priceLabel")} id="event-price"><PriceTag e={e} className="font-display text-2xl font-black" /> {!e.is_free && <span className="text-xs text-muted-foreground">{t("ev.perPerson")}</span>}</Row>
                {!!e.options?.length && (
                  <Row label={t("ev.options")} id="event-options">
                    <ul className="grid gap-1.5">{e.options.map((o) => <li key={o.option_id} className="flex justify-between rounded-xl bg-accent/60 px-3 py-1.5"><span>{optLabel(o, t)}</span><b dir="ltr">{Number(o.price.amount).toLocaleString("en-US", { minimumFractionDigits: o.price.currency === "USD" ? 2 : 0 })} {o.price.currency}</b>{o.conditions && <span className="block text-xs text-muted-foreground">{o.conditions}</span>}</li>)}</ul>
                  </Row>
                )}
                {!!e.seating_types?.length && (
                  <Row label={t("seat.available")} id="event-seating">
                    <span className="flex flex-wrap gap-1.5">{e.seating_types.map((s) => <SeatingBadge key={s} s={s} testId={`event-seating-badge-${s}`} />)}</span>
                    {e.vip_fee && e.seating_types.includes("vip") && <span className="block text-xs text-muted-foreground mt-1" data-testid="event-vip-fee" dir="auto">VIP {vipFeeText(e.vip_fee, e.vip_fee_per, t)}</span>}
                  </Row>
                )}
                {e.price_includes && <Row label={t("ev.includes")} id="event-includes">{e.price_includes}</Row>}
                {e.charged_separately && <Row label={t("ev.separately")} id="event-separately">{e.charged_separately}</Row>}
                {e.requirements && <Row label={t("ev.requirements")} id="event-requirements">{e.requirements}</Row>}
                {e.terms && <Row label={t("ev.terms")} id="event-terms">{e.terms}</Row>}
                {(user && user.booking_restricted?.active) && <p data-testid="event-restricted-note" className="text-xs font-semibold text-primary">{t("b6.restrictedMsg")}</p>}
                <button data-testid="event-book-button" disabled={e.request_status !== "open" || !!(user && user.booking_restricted?.active)} onClick={book} className="w-full h-12 rounded-full bg-primary text-primary-foreground font-bold disabled:opacity-50">
                  {e.request_status === "open" ? t("ev.request") : t(`ev.${e.request_status}Badge`)}
                </button>
              </div>
            </aside>
          </div>
        )}
      </main>
      {e && open && <BookingDialog event={e} onClose={() => setOpen(false)} />}
    </div>
  );
}
